import React from "react";
import { useAppDispatch, useAppSelector } from "../../../../../../../redux";
import { getPartnerIntegrationStatusSagaAction } from "../../../../../../../redux/saga/integration-status/saga-actions";
import { getPartnerTypesSagaAction } from "../../../../../../../redux/saga/partner-details/saga-actions";
import { getUserListHIDSagaAction } from "../../../../../../../redux/saga/user/saga-actions";
import {
  INTEGRATION_FILTER_ENDORSEMENT_LEVEL_OPTIONS,
  INTEGRATION_FILTER_INTEGRATION_STAGE_OPTIONS,
  INTEGRATION_FILTER_INTEGRATION_STATUS_OPTIONS,
  INTEGRATION_FILTER_INTEGRATION_TYPE_OPTIONS,
  INTEGRATION_FILTER_REGIONS,
  INTEGRATION_FILTER_VALIDATION_SCORE_OPERATORS,
  INTEGRATION_FILTER_VALIDATION_STATUS_OPTIONS,
} from "./integrationFilterConstants";

const DEFAULT_INTEGRATION_STATUS_PAGE_SIZE = 20;

const formatConsultantName = (firstName: string, lastName: string): string =>
  `${firstName} ${lastName}`.trim();

export type ConsultantFilterOption = {
  label: string;
  value: string;
};

export const useIntegrationFilterOptions = () => {
  const dispatch = useAppDispatch();
  const { partnerTypes } = useAppSelector((state) => state.partner_details);
  const { userListHID } = useAppSelector((state) => state.user);
  const { tableColumns } = useAppSelector((state) => state.integration_status);

  React.useEffect(() => {
    if (partnerTypes.length === 0) {
      dispatch(getPartnerTypesSagaAction());
    }
    if (userListHID.length === 0) {
      dispatch(getUserListHIDSagaAction());
    }
    if (tableColumns.length === 0) {
      dispatch(
        getPartnerIntegrationStatusSagaAction({
          pagination: { page: 1, limit: DEFAULT_INTEGRATION_STATUS_PAGE_SIZE },
        })
      );
    }
  }, [dispatch, partnerTypes.length, userListHID.length, tableColumns.length]);

  const partnerTypeOptions = React.useMemo(
    () => partnerTypes.map((type) => type.value),
    [partnerTypes]
  );

  const consultantOptions = React.useMemo<ConsultantFilterOption[]>(
    () =>
      userListHID
        .map((user) => ({
          label: formatConsultantName(user.first_name, user.last_name),
          value: String(user.ID),
        }))
        .filter((option) => option.label)
        .sort((a, b) => a.label.localeCompare(b.label)),
    [userListHID]
  );

  const technologyOptions = React.useMemo(
    () =>
      Array.from(new Set(tableColumns.map((column) => column.label))).sort(
        (a, b) => a.localeCompare(b)
      ),
    [tableColumns]
  );

  return {
    regionOptions: [...INTEGRATION_FILTER_REGIONS],
    partnerTypeOptions,
    consultantOptions,
    technologyOptions,
    validationStatusOptions: [...INTEGRATION_FILTER_VALIDATION_STATUS_OPTIONS],
    validationScoreOperators: [...INTEGRATION_FILTER_VALIDATION_SCORE_OPERATORS],
    endorsementLevelOptions: [...INTEGRATION_FILTER_ENDORSEMENT_LEVEL_OPTIONS],
    integrationTypeOptions: [...INTEGRATION_FILTER_INTEGRATION_TYPE_OPTIONS],
    integrationStatusOptions: [...INTEGRATION_FILTER_INTEGRATION_STATUS_OPTIONS],
    integrationStageOptions: [...INTEGRATION_FILTER_INTEGRATION_STAGE_OPTIONS],
  };
};
